import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Star,
  Minus,
  Plus,
  ShoppingBag,
  Truck,
  ShieldCheck,
  Sparkles,
  Volume2,
  Repeat,
  Droplets,
  PawPrint,
  Check,
  Heart,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  COLORS,
  SIZES,
  HERO_IMAGE,
  PRODUCT_VIDEO,
  LIFESTYLE_1,
  LIFESTYLE_2,
  LIFESTYLE_GALLERY,
  FAQS,
} from "@/data/product";

const Stars = ({ n = 5, size = 16 }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={2}
        className={i < n ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "text-muted-foreground/40"}
      />
    ))}
  </div>
);

export default function Landing() {
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [color, setColor] = useState("blue");
  const [size, setSize] = useState("medium");
  const [qty, setQty] = useState(1);
  // Gallery active image index: 0..2 = lifestyle, 3+ = product (auto-tracks color)
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/product").then((r) => setProduct(r.data));
    api.get("/reviews").then((r) => setReviews(r.data));
  }, []);

  const activeColor = useMemo(() => COLORS.find((c) => c.id === color), [color]);
  const galleryItems = useMemo(
    () => [
      ...LIFESTYLE_GALLERY.map((url) => ({ type: "lifestyle", url })),
      ...COLORS.map((c) => ({ type: "product", url: c.image, colorId: c.id, label: c.label })),
    ],
    []
  );
  const activeGallery = galleryItems[galleryIdx] ?? galleryItems[0];
  const onSelectGallery = (i) => {
    setGalleryIdx(i);
    const item = galleryItems[i];
    if (item?.type === "product" && item.colorId) setColor(item.colorId);
  };
  const onSelectColor = (id) => {
    setColor(id);
    const idx = galleryItems.findIndex((g) => g.type === "product" && g.colorId === id);
    if (idx >= 0) setGalleryIdx(idx);
  };
  const list = product?.list_price ?? 34.99;
  const sale = product?.sale_price ?? 19.99;
  const savings = Math.round(((list - sale) / list) * 100);
  const totalNum = Number((sale * qty).toFixed(2));
  const total = totalNum.toFixed(2);
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "4.9";

  const trackFunnel = (eventName) => {
    const payload = {
      value: totalNum,
      currency: "USD",
      content_ids: ["pawventure-rugby-ball"],
      content_type: "product",
      content_name: "Pawventure Squeaky Rugby Ball",
      contents: [{ id: "pawventure-rugby-ball", quantity: qty, item_price: sale }],
      num_items: qty,
    };
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", eventName, payload);
    }
    if (typeof window !== "undefined" && window.gtag) {
      const gaEvent = eventName === "InitiateCheckout" ? "begin_checkout" : "add_payment_info";
      window.gtag("event", gaEvent, {
        value: totalNum,
        currency: "USD",
        items: [{ item_id: "pawventure-rugby-ball", item_name: "Pawventure Squeaky Rugby Ball", price: sale, quantity: qty, item_variant: `${color} / ${size}` }],
      });
    }
  };

  const openCheckout = () => {
    trackFunnel("InitiateCheckout");
    setShowCheckout(true);
  };

  const handleBuy = async () => {
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email");
      return;
    }
    trackFunnel("AddPaymentInfo");
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        color, size, quantity: qty,
        email: form.email, name: form.name,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Try again.");
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_fetch-toy-store/artifacts/xq3326bd_Gemini_Generated_Image_sijaotsijaotsija.png"
              alt="Pawventure Essentials"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-border"
            />
            <span className="font-display font-bold text-xl tracking-tight leading-none hidden sm:inline">Pawventure <span className="text-primary">Essentials</span></span>
            <span className="font-display font-bold text-lg leading-none sm:hidden">Pawventure</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#reviews" className="hover:text-foreground transition-colors">Reviews</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <Button
            data-testid="header-buy-button"
            onClick={() => document.getElementById("buy-section")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full font-semibold"
          >
            <ShoppingBag size={16} className="mr-2" strokeWidth={2.5} /> Buy now
          </Button>
        </div>
      </header>

      {/* HERO / BUY SECTION */}
      <section id="buy-section" className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 lg:pt-16 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Gallery */}
          <div className="lg:sticky lg:top-24 space-y-4 animate-fade-up">
            <Card className="relative overflow-hidden rounded-3xl border-border bg-card aspect-square dot-grid-bg p-4">
              <img
                key={galleryIdx}
                src={activeGallery.url}
                alt={activeGallery.type === "product" ? `${activeGallery.label} rugby ball` : "Dog with rugby ball"}
                className={
                  activeGallery.type === "product"
                    ? "w-full h-full object-contain p-6 animate-float-ball"
                    : "w-full h-full object-cover rounded-2xl shadow-lg animate-shake-frame"
                }
                data-testid="active-product-image"
              />
              <Badge className="absolute top-5 left-5 rounded-full bg-primary text-primary-foreground border-0 px-3 py-1 font-semibold z-10" data-testid="discount-badge">
                Save {savings}%
              </Badge>
            </Card>
            <div className="grid grid-cols-6 gap-2.5">
              {galleryItems.map((g, i) => (
                <button
                  key={i}
                  data-testid={g.type === "product" ? `thumb-${g.colorId}` : `thumb-lifestyle-${i}`}
                  onClick={() => onSelectGallery(i)}
                  className={`aspect-square rounded-xl bg-card border-2 overflow-hidden transition-all hover:-translate-y-0.5 ${
                    galleryIdx === i ? "border-primary shadow-md" : "border-border"
                  }`}
                >
                  <img
                    src={g.url}
                    alt={g.type === "product" ? g.label : "lifestyle"}
                    className={`w-full h-full ${g.type === "product" ? "object-contain p-1.5" : "object-cover"}`}
                  />
                </button>
              ))}
            </div>
            <Card className="rounded-3xl border-border overflow-hidden">
              <video
                src={PRODUCT_VIDEO}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                data-testid="product-video"
              />
            </Card>
          </div>

          {/* Product details */}
          <div className="space-y-7 animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center gap-2 text-sm">
              <Stars n={5} size={16} />
              <span className="font-semibold text-foreground" data-testid="avg-rating">{avg}</span>
              <span className="text-muted-foreground">({reviews.length || 6}+ reviews)</span>
            </div>

            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid="product-title">
                The squeaky <span className="text-primary">rugby ball</span> dogs go wild for.
              </h1>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed" data-testid="product-subtitle">
                Built-in squeaker, unpredictable bounce, anti-bite TPR. Designed for fetch obsessives,
                heavy chewers, pool days and backyard zoomies.
              </p>
            </div>

            <div className="flex items-baseline gap-3" data-testid="price-block">
              <span className="text-4xl font-display font-bold text-foreground">${sale}</span>
              <span className="text-xl text-muted-foreground line-through">${list}</span>
              <Badge variant="secondary" className="rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-0 font-semibold">
                You save ${(list - sale).toFixed(2)}
              </Badge>
            </div>

            {/* Color picker */}
            <div className="space-y-3" data-testid="color-picker-block">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-bold uppercase tracking-wide">Color</Label>
                <span className="text-sm text-muted-foreground capitalize">{activeColor?.label}</span>
              </div>
              <div className="flex gap-3">
                {COLORS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`color-picker-${c.id}`}
                    onClick={() => onSelectColor(c.id)}
                    aria-label={c.label}
                    className={`w-12 h-12 rounded-full border-2 transition-all ${
                      color === c.id
                        ? "ring-4 ring-primary/30 border-primary scale-110"
                        : "border-border hover:scale-105"
                    }`}
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
            </div>

            {/* Size selector */}
            <div className="space-y-3" data-testid="size-picker-block">
              <Label className="text-sm font-bold uppercase tracking-wide">Dog size</Label>
              <div className="grid grid-cols-3 gap-3">
                {SIZES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`size-selector-${s.id}`}
                    onClick={() => setSize(s.id)}
                    className={`text-left rounded-2xl border-2 p-4 transition-all ${
                      size === s.id
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <div className="font-display font-semibold text-base">{s.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="space-y-3">
              <Label className="text-sm font-bold uppercase tracking-wide">Quantity</Label>
              <div className="inline-flex items-center bg-card border-2 border-border rounded-full p-1">
                <Button
                  data-testid="qty-decrement"
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                >
                  <Minus size={16} strokeWidth={2.5} />
                </Button>
                <span className="w-10 text-center font-display font-semibold" data-testid="qty-value">{qty}</span>
                <Button
                  data-testid="qty-increment"
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setQty((q) => Math.min(20, q + 1))}
                >
                  <Plus size={16} strokeWidth={2.5} />
                </Button>
              </div>
            </div>

            <Button
              data-testid="buy-now-button"
              onClick={openCheckout}
              className="w-full h-14 text-base rounded-full font-bold shadow-lg shadow-primary/25 hover:-translate-y-0.5 transition-transform active:scale-95"
            >
              <ShoppingBag size={18} className="mr-2" strokeWidth={2.5} />
              Buy now — ${total}
            </Button>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-2 pt-2" data-testid="trust-badges">
              {[
                { icon: Truck, label: "Free US shipping" },
                { icon: ShieldCheck, label: "Secure checkout" },
                { icon: Heart, label: "30-day returns" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center text-center gap-1.5 p-3 rounded-2xl bg-card border border-border">
                  <Icon size={20} className="text-primary" strokeWidth={2.5} />
                  <span className="text-xs font-semibold text-muted-foreground leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE BAND */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-16 lg:pb-24">
        <Card className="relative overflow-hidden rounded-[2rem] border-border">
          <img src={HERO_IMAGE} alt="Happy puppy with rugby ball" className="w-full h-[320px] sm:h-[460px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/85 via-background/40 to-transparent" />
          <div className="absolute inset-0 flex items-center px-8 sm:px-14 max-w-2xl">
            <div className="animate-fade-up">
              <Badge className="rounded-full bg-secondary text-secondary-foreground border-0 mb-4">Loved by 12,000+ dogs</Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-tight mb-3">
                Your dog is one fetch away from happy.
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-md">
                The asymmetric rugby shape sends it bouncing in every direction — turning a flat fetch into an obsession.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 lg:pb-24">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <Badge className="rounded-full bg-primary/10 text-primary border-0 mb-4">Built different</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
            Why dogs <span className="text-primary">love it</span>.
          </h2>
          <p className="mt-4 text-muted-foreground">Engineered for chew, bounce, fetch, and float. Vet-approved materials, dog-tested designs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="features-bento">
          {/* Big feature card with image */}
          <Card className="md:col-span-2 md:row-span-2 rounded-3xl border-border overflow-hidden p-0 group hover:-translate-y-1 transition-transform">
            <div className="relative aspect-[16/10] md:aspect-auto md:h-full">
              <img src={LIFESTYLE_1} alt="Dog running with ball" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <Sparkles className="text-primary mb-3" strokeWidth={2.5} />
                <h3 className="text-2xl sm:text-3xl font-display font-bold mb-2">Anti-bite TPR</h3>
                <p className="text-muted-foreground max-w-md">
                  Thermoplastic rubber that flexes under teeth but won't tear. Survives daily wars
                  with the family Lab. Non-toxic and dishwasher-safe (top rack only).
                </p>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-border p-7 hover:-translate-y-1 transition-transform">
            <Volume2 className="text-secondary mb-4" size={28} strokeWidth={2.5} />
            <h3 className="font-display font-bold text-xl mb-2">Built-in squeaker</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Every bite, every catch — a satisfying squeak that triggers natural prey drive.
            </p>
          </Card>

          <Card className="rounded-3xl border-border p-7 hover:-translate-y-1 transition-transform">
            <Repeat className="text-primary mb-4" size={28} strokeWidth={2.5} />
            <h3 className="font-display font-bold text-xl mb-2">Wonky bounce</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The rugby shape never bounces the same way twice. Pure unpredictable joy.
            </p>
          </Card>

          <Card className="rounded-3xl border-border p-7 hover:-translate-y-1 transition-transform">
            <Droplets className="text-secondary mb-4" size={28} strokeWidth={2.5} />
            <h3 className="font-display font-bold text-xl mb-2">Pool & lake ready</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Water-resistant and bright. Floats high, easy to spot, easy to clean.
            </p>
          </Card>

          <Card className="rounded-3xl border-border p-7 hover:-translate-y-1 transition-transform">
            <PawPrint className="text-primary mb-4" size={28} strokeWidth={2.5} />
            <h3 className="font-display font-bold text-xl mb-2">Every size dog</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Three sizes for everyone from teacup pups to gentle giants.
            </p>
          </Card>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-3 gap-10 mb-10 items-end">
          <div className="lg:col-span-2">
            <Badge className="rounded-full bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent-foreground))] border-0 mb-4">Wagging reviews</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
              {avg} out of 5 paws.
            </h2>
            <p className="mt-3 text-muted-foreground">{reviews.length}+ verified reviews from dogs (and their humans).</p>
          </div>
          <div className="flex lg:justify-end">
            <Stars n={5} size={28} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
          {reviews.map((r) => (
            <Card key={r.id} data-testid={`review-card-${r.id}`} className="rounded-3xl border-border p-6 hover:-translate-y-1 transition-transform">
              <Stars n={r.rating} size={14} />
              <h3 className="font-display font-semibold text-lg mt-3">{r.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2 line-clamp-5">{r.body}</p>
              <Separator className="my-4" />
              <div className="flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-foreground">{r.author}</div>
                  <div className="text-muted-foreground">{r.date}</div>
                </div>
                {r.verified && (
                  <Badge variant="secondary" className="rounded-full bg-primary/10 text-primary border-0 gap-1">
                    <Check size={12} strokeWidth={3} /> Verified
                  </Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 lg:pb-24">
        <div className="text-center mb-10">
          <Badge className="rounded-full bg-secondary/10 text-secondary border-0 mb-4">Questions, answered</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">FAQ</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="rounded-2xl bg-card border border-border px-5 data-[state=open]:border-primary/40"
            >
              <AccordionTrigger className="font-display font-semibold text-left hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <Card className="rounded-[2rem] border-0 bg-primary text-primary-foreground overflow-hidden relative p-10 sm:p-14">
          <img src={LIFESTYLE_2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-tight mb-4">
              Make every fetch a tail-wagging event.
            </h2>
            <p className="text-primary-foreground/90 text-lg mb-7 max-w-lg">
              Try the rugby ball your dog will refuse to drop. 30-day money-back guarantee.
            </p>
            <Button
              data-testid="cta-bottom-buy"
              variant="secondary"
              onClick={() => document.getElementById("buy-section")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full h-14 px-8 text-base font-bold bg-white text-primary hover:bg-white/90 hover:-translate-y-0.5 transition-transform"
            >
              Get yours for ${sale}
              <ShoppingBag size={18} className="ml-2" strokeWidth={2.5} />
            </Button>
          </div>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_fetch-toy-store/artifacts/xq3326bd_Gemini_Generated_Image_sijaotsijaotsija.png"
                  alt="Pawventure Essentials"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="font-display font-bold">Pawventure Essentials</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
                Anti-bite TPR squeaky rugby balls for dogs of every size. Built for fetch, training,
                and zoomies. Free US shipping.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-sm mb-3">Shop</h3>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#reviews" className="hover:text-foreground transition-colors">Reviews</a></li>
                <li><a href="#faq" className="hover:text-foreground transition-colors">FAQ</a></li>
                <li><a href="/about" className="hover:text-foreground transition-colors" data-testid="footer-about-link">About us</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-display font-semibold text-sm mb-3">Help</h3>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><a href="/return-policy" className="hover:text-foreground transition-colors" data-testid="footer-return-policy-link">Return Policy</a></li>
                <li><a href="/shipping-policy" className="hover:text-foreground transition-colors" data-testid="footer-shipping-policy-link">Shipping Policy</a></li>
                <li><a href="mailto:anish.sudarsan@gmail.com" className="hover:text-foreground transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-display font-semibold text-sm mb-3">Legal</h3>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><a href="/privacy-policy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© 2026 Pawventure Essentials. Made for very good dogs.</p>
            <p>Questions? <a href="mailto:anish.sudarsan@gmail.com" className="text-primary font-semibold">anish.sudarsan@gmail.com</a></p>
          </div>
        </div>
      </footer>

      {/* Checkout dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Almost there!</DialogTitle>
            <DialogDescription>
              We'll send your order confirmation here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-2xl bg-muted/40 border border-border p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{qty}× Rugby Ball ({activeColor?.label}, {size})</span>
                <span className="font-semibold">${total}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                data-testid="checkout-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jamie Doe"
                className="rounded-full h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="checkout-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className="rounded-full h-11"
              />
            </div>
            <Button
              data-testid="checkout-submit"
              disabled={loading}
              onClick={handleBuy}
              className="w-full h-12 rounded-full font-bold"
            >
              {loading ? "Redirecting..." : `Pay $${total} securely`}
            </Button>
            <p className="text-xs text-muted-foreground text-center flex items-center gap-1 justify-center">
              <ShieldCheck size={12} strokeWidth={2.5} /> Powered by Stripe — encrypted & secure.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
